import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Package,
  Quote,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import { useProducts } from "../../context/ProductContext";
import {
  productDetailPathSegment,
  getStorefrontPrices,
} from "../../utils/productVariants";
import { fetchBannersPublic } from "../../services/cmsClient";
import Seo from "../common/Seo";

const effectiveSalePrice = (p) => getStorefrontPrices(p).salePrice;

const formatRupee = (n) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return `₹${Math.round(x).toLocaleString("en-IN")}`;
};

const CATEGORY_LINKS = [
  { text: "GI Tagged", type: "GI_TAGS", image: "image2.avif", accent: "from-amber-500/20 to-rose-500/10" },
  { text: "Handicrafts", type: "HANDICRAFTS", image: "image3.avif", accent: "from-violet-500/20 to-fuchsia-500/10" },
  { text: "Culture & Art", type: "ART", image: "imagew1.avif", accent: "from-emerald-500/20 to-teal-500/10" },
];

const TESTIMONIALS = [
  {
    name: "Aarav Sharma",
    role: "Patna",
    text: "Absolutely loved the handmade items. Quality was top-notch and delivery was prompt!",
  },
  {
    name: "Meera Jain",
    role: "Regular shopper",
    text: "A perfect place to discover GI-tagged products. Authentic and beautiful!",
  },
  {
    name: "Riya Kapoor",
    role: "Gift buyer",
    text: "Found unique pieces for family gifts—checkout was smooth and the packaging felt premium.",
  },
];

const WHY_ITEMS = [
  { title: "Authentic GI & crafts", body: "Curated makers and verified listings you can trust.", Icon: BadgeCheck },
  { title: "Fast, careful delivery", body: "Orders packed with care and tracked to your door.", Icon: Truck },
  { title: "Secure checkout", body: "Encrypted payments and clear order updates.", Icon: ShieldCheck },
  { title: "Human support", body: "Questions? Our team actually replies.", Icon: Headphones },
];

const HomePage = () => {
  const { allProducts, loading: productsLoading } = useProducts();
  const [banners, setBanners] = useState([]);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    let alive = true;
    fetchBannersPublic()
      .then((items) => {
        if (alive) setBanners(items);
      })
      .catch(() => {
        if (alive) setBanners([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (TESTIMONIALS.length <= 1) return;
    const id = window.setInterval(() => {
      setTestimonialIndex((i) => (i + 1) % TESTIMONIALS.length);
    }, 7200);
    return () => window.clearInterval(id);
  }, []);

  const goTestimonial = useCallback((delta) => {
    setTestimonialIndex((i) => {
      const n = TESTIMONIALS.length;
      return (i + delta + n) % n;
    });
  }, []);

  const heroBanner = banners[0];
  const trending = allProducts.slice(0, 6);
  const t = TESTIMONIALS[testimonialIndex];

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-rose-50/30 text-stone-900">
      <Seo
        title="Discover local makers & authentic products"
        description="Browse GI-tagged goods, handicrafts, and curated picks from trusted vendors. Shop the marketplace in one place."
        canonical="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-rose-100/80">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-30%,rgba(251,113,133,0.35),transparent)]" />
        <div className="pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full bg-rose-300/30 blur-3xl animate-floatSoft" />
        <div
          className="pointer-events-none absolute -right-20 top-40 h-64 w-64 rounded-full bg-amber-300/25 blur-3xl animate-floatSoft"
          style={{ animationDelay: "1.2s" }}
        />

        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-16 lg:grid-cols-[1.08fr_0.92fr] lg:px-8">
          <div className="text-center lg:text-left [animation-delay:80ms] opacity-0 animate-fadeInUp">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-rose-200/80 bg-white/70 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-rose-600 shadow-sm backdrop-blur-sm">
              <Sparkles className="h-3.5 w-3.5" aria-hidden />
              Multi-vendor marketplace
            </p>

            {heroBanner ? (
              <>
                <h1 className="text-4xl font-extrabold tracking-tight text-stone-950 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
                  {heroBanner.title}
                </h1>
                {heroBanner.subtitle ? (
                  <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg lg:mx-0">
                    {heroBanner.subtitle}
                  </p>
                ) : null}
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                  <Link
                    to={heroBanner.ctaUrl?.trim() || "/shop"}
                    className="group inline-flex items-center gap-2 rounded-full bg-stone-950 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-stone-900/20 transition hover:-translate-y-0.5 hover:bg-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
                  >
                    {heroBanner.ctaLabel || "Start shopping"}
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
                  </Link>
                  <Link
                    to="/shop"
                    className="inline-flex items-center rounded-full border border-stone-200/90 bg-white/80 px-6 py-3.5 text-sm font-semibold text-stone-800 backdrop-blur-sm transition hover:border-rose-200 hover:bg-white hover:text-rose-700"
                  >
                    Browse catalog
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-4xl font-extrabold tracking-tight text-stone-950 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
                  Empower local artisans. Explore authentic products.
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg lg:mx-0">
                  Discover GI-tagged goods, handcrafted pieces, and stories behind every seller—all in one
                  modern storefront.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                  <Link
                    to="/shop"
                    className="group inline-flex items-center gap-2 rounded-full bg-stone-950 px-7 py-3.5 text-sm font-bold text-white shadow-lg shadow-stone-900/20 transition hover:-translate-y-0.5 hover:bg-stone-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
                  >
                    Start shopping
                    <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" aria-hidden />
                  </Link>
                  <Link
                    to="/about"
                    className="inline-flex items-center rounded-full border border-stone-200/90 bg-white/80 px-6 py-3.5 text-sm font-semibold text-stone-800 backdrop-blur-sm transition hover:border-rose-200 hover:bg-white"
                  >
                    Our story
                  </Link>
                </div>
              </>
            )}
          </div>

          <div className="relative mx-auto w-full max-w-lg lg:max-w-none [animation-delay:180ms] opacity-0 animate-fadeInUp">
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-rose-200/50 via-amber-100/40 to-violet-200/40 blur-2xl" />
            <div className="relative overflow-hidden rounded-[1.75rem] border border-white/80 bg-white/40 shadow-2xl shadow-rose-900/10 ring-1 ring-stone-200/60 backdrop-blur-md">
              {heroBanner?.imageUrl ? (
                <img
                  src={heroBanner.imageUrl}
                  alt={heroBanner.title || "Featured"}
                  className="aspect-[4/3] w-full object-cover transition duration-700 ease-out hover:scale-[1.02]"
                />
              ) : (
                <div className="flex aspect-[4/3] flex-col items-center justify-center gap-3 bg-gradient-to-br from-rose-50 to-amber-50 p-8 text-center">
                  <Package className="h-14 w-14 text-rose-300" strokeWidth={1.25} aria-hidden />
                  <p className="max-w-xs text-sm font-medium text-stone-500">
                    Add a hero image from the admin panel to showcase your campaign here.
                  </p>
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-stone-900/50 to-transparent p-5 pt-16">
                <p className="text-xs font-semibold uppercase tracking-wider text-white/90">Live picks</p>
                <p className="text-sm text-white/80">Updated as vendors publish new listings.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <div className="border-b border-stone-100 bg-white/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-4 py-3.5 text-xs font-semibold uppercase tracking-wider text-stone-500 sm:justify-between sm:px-6 lg:px-8">
          <span className="flex items-center gap-2 text-stone-600">
            <BadgeCheck className="h-4 w-4 text-emerald-500" aria-hidden />
            Verified sellers
          </span>
          <span className="hidden h-4 w-px bg-stone-200 sm:block" aria-hidden />
          <span className="flex items-center gap-2 text-stone-600">
            <Truck className="h-4 w-4 text-rose-500" aria-hidden />
            Delivery you can track
          </span>
          <span className="hidden h-4 w-px bg-stone-200 sm:block" aria-hidden />
          <span className="flex items-center gap-2 text-stone-600">
            <ShieldCheck className="h-4 w-4 text-violet-500" aria-hidden />
            Secure payments
          </span>
        </div>
      </div>

      {/* Categories */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-2 text-center sm:mb-12 sm:text-left">
          <h2 className="text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
            Shop by category
          </h2>
          <p className="text-sm text-stone-600 sm:text-base">
            Jump into a collection—filters apply instantly on the shop page.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-3 sm:gap-5">
          {CATEGORY_LINKS.map((item, index) => (
            <Link
              key={item.type}
              to={`/shop?type=${encodeURIComponent(item.type)}`}
              className="group relative overflow-hidden rounded-2xl border border-stone-200/80 bg-white p-1 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-rose-200/80 hover:shadow-xl hover:shadow-rose-100/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
              style={{ animationDelay: `${index * 90}ms` }}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${item.accent} opacity-0 transition duration-300 group-hover:opacity-100`}
              />
              <div className="relative flex flex-col overflow-hidden rounded-[0.9rem]">
                <div className="relative aspect-[5/3] overflow-hidden bg-stone-100">
                  <img
                    src={`/${item.image}`}
                    alt={item.text}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/55 via-transparent to-transparent" />
                  <span className="absolute bottom-3 left-3 right-3 text-lg font-bold text-white drop-shadow-sm">
                    {item.text}
                  </span>
                </div>
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-semibold uppercase tracking-wide text-rose-600">
                    Explore
                  </span>
                  <ArrowRight className="h-4 w-4 text-stone-400 transition group-hover:translate-x-1 group-hover:text-rose-600" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Trending */}
      <section className="border-y border-rose-100/70 bg-gradient-to-br from-rose-50/90 via-white to-amber-50/40 py-14 sm:py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <h2 className="text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
                Trending now
              </h2>
              <p className="mt-1 max-w-lg text-sm text-stone-600 sm:text-base">
                A live slice of the catalog—tap a card for details and checkout.
              </p>
            </div>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white px-5 py-2.5 text-sm font-semibold text-stone-800 shadow-sm transition hover:border-rose-200 hover:text-rose-700"
            >
              View all products
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {productsLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((k) => (
                <div
                  key={k}
                  className="animate-pulse overflow-hidden rounded-2xl border border-stone-100 bg-white p-4"
                >
                  <div className="mb-4 aspect-[4/3] rounded-xl bg-stone-200" />
                  <div className="mb-2 h-4 w-3/4 rounded bg-stone-200" />
                  <div className="h-3 w-1/3 rounded bg-stone-100" />
                </div>
              ))}
            </div>
          ) : trending.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-stone-200 bg-white/80 px-6 py-14 text-center">
              <Package className="mx-auto mb-3 h-10 w-10 text-stone-300" />
              <p className="font-semibold text-stone-800">Products are on the way</p>
              <p className="mt-1 text-sm text-stone-600">Check back soon or head to the shop.</p>
              <Link
                to="/shop"
                className="mt-6 inline-flex rounded-full bg-stone-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-stone-800"
              >
                Open shop
              </Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {trending.map((p) => {
                const { salePrice: sale, listPrice: list, hasDiscount: showStrike } =
                  getStorefrontPrices(p);
                const img = p.images?.[0];
                return (
                  <Link
                    key={p._id}
                    to={{ pathname: `/products/${productDetailPathSegment(p)}` }}
                    state={{ product: p }}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-rose-200/80 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-2"
                  >
                    <div className="relative aspect-[5/3] overflow-hidden bg-stone-100">
                      {img ? (
                        <img
                          src={img}
                          alt={p.name || "Product"}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center bg-gradient-to-br from-stone-100 to-stone-50">
                          <Package className="h-10 w-10 text-stone-300" />
                        </div>
                      )}
                      <span className="absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-rose-600 shadow-sm backdrop-blur-sm">
                        Popular
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-4">
                      <h3 className="line-clamp-2 font-semibold text-stone-900 group-hover:text-rose-700">
                        {p.name}
                      </h3>
                      <div className="mt-auto flex items-baseline justify-between gap-2 pt-3">
                        <p className="text-lg font-bold text-stone-900">{formatRupee(sale)}</p>
                        {showStrike ? (
                          <p className="text-xs text-stone-400 line-through">{formatRupee(list)}</p>
                        ) : null}
                      </div>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 opacity-0 transition group-hover:opacity-100">
                        View details
                        <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Why choose us */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <h2 className="mb-10 text-center text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
          Why shoppers choose us
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_ITEMS.map(({ title, body, Icon }) => (
            <div
              key={title}
              className="rounded-2xl border border-stone-100 bg-white p-6 shadow-sm transition hover:border-rose-100 hover:shadow-md"
            >
              <div className="mb-4 inline-flex rounded-xl bg-rose-50 p-2.5 text-rose-600 ring-1 ring-rose-100">
                <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
              </div>
              <h3 className="font-bold text-stone-900">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-stone-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials — carousel */}
      <section className="border-t border-stone-100 bg-gradient-to-b from-sky-50/80 to-white py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <h2 className="mb-8 text-center text-2xl font-extrabold tracking-tight text-stone-900 sm:text-3xl">
            Loved by the community
          </h2>

          <div className="relative rounded-3xl border border-stone-200/80 bg-white p-8 shadow-xl shadow-stone-200/50 sm:p-10">
            <Quote
              className="absolute right-8 top-8 h-10 w-10 text-rose-100 sm:h-12 sm:w-12"
              aria-hidden
            />
            <blockquote className="relative min-h-[8.5rem]">
              <p
                key={testimonialIndex}
                className="text-lg leading-relaxed text-stone-700 sm:text-xl animate-fadeInUp"
              >
                “{t.text}”
              </p>
            </blockquote>
            <footer className="mt-8 flex items-center justify-between gap-4 border-t border-stone-100 pt-6">
              <div>
                <p className="font-bold text-stone-900">{t.name}</p>
                <p className="text-sm text-stone-500">{t.role}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => goTestimonial(-1)}
                  className="rounded-full border border-stone-200 p-2 text-stone-600 transition hover:bg-stone-50 hover:text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                  aria-label="Previous testimonial"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={() => goTestimonial(1)}
                  className="rounded-full border border-stone-200 p-2 text-stone-600 transition hover:bg-stone-50 hover:text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
                  aria-label="Next testimonial"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </footer>

            <div className="mt-6 flex justify-center gap-2">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setTestimonialIndex(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === testimonialIndex ? "w-8 bg-rose-500" : "w-2 bg-stone-200 hover:bg-stone-300"
                  }`}
                  aria-label={`Show testimonial ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-stone-950 px-6 py-12 text-center sm:px-10 sm:py-14">
          <div className="pointer-events-none absolute -left-20 top-0 h-64 w-64 rounded-full bg-rose-500/25 blur-3xl" />
          <div className="pointer-events-none absolute -right-10 bottom-0 h-48 w-48 rounded-full bg-amber-400/20 blur-3xl" />
          <h2 className="relative text-2xl font-extrabold text-white sm:text-3xl">
            Ready to explore the full catalog?
          </h2>
          <p className="relative mx-auto mt-3 max-w-lg text-sm text-stone-300 sm:text-base">
            Filters, wishlist, and checkout work the same across every vendor on the platform.
          </p>
          <Link
            to="/shop"
            className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-bold text-stone-950 shadow-lg transition hover:bg-rose-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-stone-900"
          >
            Go to shop
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
